import React, { useState } from 'react'

import Button from '../../Button'
import Dialog, { DialogBody, DialogContent, DialogHeader } from '../../Dialog'
import { ClickableLink } from '../../helpers/ClickableLink'
import { DeltaInput } from '../../Login-Styles'

import useInstantOnboarding from '../../../hooks/useInstantOnboarding'
import { BackendRemote } from '../../../backend-com'

import styles from './styles.module.scss'

import type { DialogProps } from '../../../contexts/DialogContext'

type Props = {
  selectedAccountId: number
}

// Accepts a bare host[:port], or a full URL pasted along with its scheme,
// path, query, fragment, etc., and returns just the host[:port] the
// webimapaccount QR payload wants. Falls back to the trimmed input as-is if
// it isn't URL-parseable, so it still hits the normal "invalid server
// address" error from checkQr instead of failing silently here.
function parseWebimapHost(input: string): string {
  const trimmed = input.trim()
  if (trimmed === '') {
    return ''
  }
  try {
    const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
    return new URL(hasScheme ? trimmed : `https://${trimmed}`).host
  } catch {
    return trimmed
  }
}

export default function UseWebimapServerDialog({
  onClose,
  selectedAccountId,
}: DialogProps & Props) {
  const { startInstantOnboardingFlow } = useInstantOnboarding()

  const [host, setHost] = useState('')
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    const parsedHost = parseWebimapHost(host)
    if (parsedHost === '') {
      setError('This does not look like a valid server address.')
      return
    }

    const url = `webimapaccount:${parsedHost}`
    let qr: any
    try {
      // checkQr throws (rather than returning another kind) when core rejects
      // the host — without the catch that's an unhandled rejection and the
      // dialog silently does nothing.
      qr = await BackendRemote.rpc.checkQr(selectedAccountId, url)
    } catch {
      setError('This does not look like a valid server address.')
      return
    }
    if (!qr || qr.kind !== 'account') {
      setError('This does not look like a valid server address.')
      return
    }

    onClose()
    await startInstantOnboardingFlow({ qr, url } as any)
  }

  return (
    <Dialog onClose={onClose}>
      <DialogHeader title='Use a madmail webimap server' onClose={onClose} />
      <DialogBody>
        <DialogContent>
          <p>
            This is even more experimental than the rest of this prototype. It
            needs NO proxy or bridge, and only works with madmail servers that
            have webimap enabled.
          </p>
          <p>
            <ClickableLink href='https://github.com/themadorg/madmail'>
              github.com/themadorg/madmail
            </ClickableLink>
            <br />
            <ClickableLink href='https://github.com/themadorg/madmail/blob/main/docs/TDD/10-webimap.md'>
              webimap docs (TDD 10-webimap)
            </ClickableLink>
          </p>
          <form onSubmit={onSubmit}>
            <DeltaInput
              key='webimapHost'
              id='webimapHost'
              placeholder='IP address or domain, e.g. 10.0.0.5 or mail.example.org'
              value={host}
              onChange={event => {
                setHost(event.target.value)
                if (error) {
                  setError(null)
                }
              }}
              autoFocus={true}
            />
            {error && <p className={styles.inputError}>{error}</p>}
            <Button
              type='submit'
              className={styles.welcomeScreenButton}
              styling='primary'
            >
              Use this server
            </Button>
          </form>
          <br /> {/* space after buttons */}
        </DialogContent>
      </DialogBody>
    </Dialog>
  )
}
